/* Financing — a faithful React port of the prototype's rFinancing screen.
 *
 * Fund the company as a public business: draw bank loans or bonds, raise equity
 * or buy back shares, and set a dividend payout policy — each a real trade-off
 * priced off the live stock and credit rating. A capital-structure overview shows
 * cash, debt, book equity, leverage/rating, stock price, shares and founder
 * ownership (with the control-band penalty).
 *
 * Every action is staged in the decisions draft (store.addLoan / addBond /
 * setEquityRaise / setBuyback / setDivRatio → d.financing) and previewed live:
 * buildWorkingGame applies the staged financing through the very same
 * applyFinancing the server runs at advance (packages/engine/src/apply.js), so
 * cash, leverage, dilution and the credit picture on screen are exactly what
 * resolves — including same-quarter builds funded by a same-quarter loan.
 * Sub-sections are plain render functions so the page never scroll-jumps. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { Engine, applyGameCfg, fmtM, fmtK } from '@venturewrist/engine';
import type { LoanTenor, BondTenor } from '@venturewrist/shared';
import { useDecisions } from '../../store/decisions';
import { buildWorkingGame } from '../../lib/designState';
import type { ScreenProps } from './types';
import '../../styles/financing.css';

const Eng = Engine as any;
const WhyChip = () => <span className="tooltip-why" title="Learn the theory">?</span>;

export function Financing({ detail }: ScreenProps) {
  const draft = useDecisions((z) => z.draft);
  const addLoan = useDecisions((z) => z.addLoan);
  const removeLoan = useDecisions((z) => z.removeLoan);
  const addBond = useDecisions((z) => z.addBond);
  const removeBond = useDecisions((z) => z.removeBond);
  const setEquityRaise = useDecisions((z) => z.setEquityRaise);
  const setBuyback = useDecisions((z) => z.setBuyback);
  const setDivRatio = useDecisions((z) => z.setDivRatio);

  const [loanTenor, setLoanTenor] = useState<LoanTenor>(8);
  const [loanAmt, setLoanAmt] = useState('');
  const [bondTenor, setBondTenor] = useState<BondTenor>(12);
  const [bondAmt, setBondAmt] = useState('');
  const [eqAmt, setEqAmt] = useState('');
  const [bbAmt, setBbAmt] = useState('');

  const g = useMemo(() => {
    const wg = buildWorkingGame(detail.state, draft);
    applyGameCfg(wg);
    Eng.ensureStock(wg); Eng.ensureFinance(wg);
    return wg;
  }, [detail.state, draft]);

  const fin = draft.financing ?? {};
  // Cash before any buyback already staged — the prototype's $2M-buffer guard
  // is checked against that, since a new amount replaces the staged one.
  const cashNoBB = useMemo(
    () => (fin.buyback ? (buildWorkingGame(detail.state, { ...draft, financing: { ...draft.financing, buyback: 0 } }) as any).cash : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail.state, draft],
  ) as number | null;
  const H = g.history as any[];
  const last = H[H.length - 1];
  const price = g.stockPrice || (last ? last.stockPrice : 10) || 10;
  const eq = Eng.bookEquity(g);
  const td = Eng.totalDebt(g);
  const de = td / Math.max(1, eq);
  const rating = Eng.creditRating(g, H);
  const pct = Eng.ownerPct(g), band = Eng.controlBand(pct);
  const cap = Eng.borrowCapacity(g, H);
  const ctrlPen = Eng.controlPenalty(g);
  const instrOn = (key: string) => !g.instruments || g.instruments[key] !== false;
  // The bundle schema caps loans and bonds at 10 each per quarter.
  const loanCapHit = (fin.loans?.length ?? 0) >= 10;
  const bondCapHit = (fin.bonds?.length ?? 0) >= 10;
  // Insolvency watch (DESIGN-insolvency-and-direct-fulfilment §6.2): debt, buybacks and
  // dividends are closed until book equity is positive; an equity raise is the way out.
  const insolvent = !!g.insolvent;
  const num = (sVal: string) => { const v = parseFloat(sVal); return isFinite(v) && v > 0 ? v : 0; };

  const tile = (l: any, v: any, sub?: any, cls?: string) => (
    <div className="card" style={{ padding: '12px 14px' }}>
      <div className="sub">{l}</div>
      <div className={'big-num ' + (cls || '')} style={{ fontSize: 20 }}>{v}</div>
      {sub ? <div className="sub">{sub}</div> : null}
    </div>
  );

  // ── capital structure ──────────────────────────────────────────────────────────
  const overview = () => (
    <>
      <div className="grid4" style={{ marginBottom: 14 }}>
        {tile('Cash', fmtM(g.cash), null, g.cash < 3e6 ? 'neg' : '')}
        {tile('Total debt', fmtM(td), td > 0 ? ((g.loan || 0) > 0 ? 'overdraft ' + fmtM(g.loan) + ' ⚠ penalty ' + ((g.revolverPenalty ?? 0.035) * 100).toFixed(1) + '%/qtr' : 'overdraft $0') + ' · term ' + fmtM(Eng.termDebt(g)) : 'debt-free')}
        {tile('Book equity', fmtM(eq), 'paid-in ' + fmtM(g.paidIn))}
        {tile('D/E · rating', de.toFixed(2) + '× · ' + rating, 'covenant ' + (g.maxDE || 2).toFixed(1) + '×')}
      </div>
      {insolvent ? (
        <div className="sub" style={{ marginBottom: 14, padding: '10px 12px', border: '1px solid var(--bad)', borderRadius: 8, color: 'var(--txt)' }}>
          🔒 <b>Insolvency watch.</b> Book equity is {fmtM(eq)}. Borrowing capacity 0 · bonds closed · dividends and buybacks suspended.
          An equity raise is available and is the fastest way out — it will dilute you (see below).
        </div>
      ) : null}
      <div className="grid3" style={{ gridTemplateColumns: '1fr 1fr 1.3fr', marginBottom: 14 }}>
        {tile('Stock price', '$' + price.toFixed(2), 'mkt cap ' + fmtM(price * g.shares))}
        {tile('Shares outstanding', fmtK(g.shares), 'you hold ' + fmtK(g.ownerShares))}
        <div className="card" style={{ padding: '12px 14px' }}>
          <div className="sub">Founder ownership<WhyChip /></div>
          <div className={'big-num ' + band.cls} style={{ fontSize: 26 }}>{Math.round(pct * 100)}%</div>
          <div className="sub"><span className={'chip ' + (band.key === 'majority' ? 'acc' : '')}>{band.label}</span> {ctrlPen > 0 ? '· score −' + Math.round(ctrlPen * 100) + '%' : '· no penalty'}</div>
        </div>
      </div>
    </>
  );

  // ── bank loans ───────────────────────────────────────────────────────────────
  const loansPanel = () => {
    if (!instrOn('loan')) return null;
    const bankDebts = (g.debts as any[]).filter((d) => d.kind !== 'bond');
    const tenors: { q: LoanTenor; label: string }[] = [{ q: 4, label: 'Short-term note' }, { q: 8, label: 'Term loan' }, { q: 12, label: 'Long-term loan' }];
    const amt = num(loanAmt);
    const quote = insolvent
      ? "Banks won't lend to a company with negative book equity."
      : !amt
      ? 'Enter an amount to see the quote.'
      : amt > cap
        ? 'Exceeds capacity (' + fmtM(cap) + '). Banks won’t lend past ' + (g.maxDE || 2).toFixed(1) + '× D/E.'
        : (() => { const rate = Eng.loanRate(g, H, loanTenor), amort = loanTenor === 12 ? 'bullet' : 'equal'; return 'Quote: ' + (rate * 100).toFixed(2) + '%/qtr for ' + loanTenor + ' qtrs · ' + (amort === 'bullet' ? 'interest only, ' + fmtM(amt) + ' balloon' : '≈ ' + fmtM(amt / loanTenor + amt * rate) + ' first-qtr payment, then falling'); })();
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <h3>🏦 Bank Loans<WhyChip /></h3>
        {bankDebts.length || (g.loan || 0) > 0 ? (
          <table className="tbl">
            <thead><tr><th>Facility</th><th className="num">Balance</th><th className="num">Rate/qtr</th><th className="num">Qtrs left</th><th>Repayment</th></tr></thead>
            <tbody>
              {(g.loan || 0) > 0 ? <tr><td>🚨 Emergency overdraft</td><td className="num neg">{fmtM(g.loan)}</td><td className="num">{((g.revolverPenalty || 0.035) * 100).toFixed(1)}%</td><td className="num">—</td><td>revolving</td></tr> : null}
              {bankDebts.map((d) => <tr key={d.id}><td>🏦 {d.label || 'Loan'}</td><td className="num">{fmtM(d.face)}</td><td className="num">{(d.rateQ * 100).toFixed(2)}%</td><td className="num">{d.qLeft}</td><td>{d.amort === 'bullet' ? 'bullet' : 'amortising'}</td></tr>)}
            </tbody>
          </table>
        ) : <div className="sub" style={{ marginBottom: 6 }}>No outstanding bank debt. Capacity under the {(g.maxDE || 2).toFixed(1)}× covenant: <b>{fmtM(cap)}</b>.</div>}
        <div style={{ marginTop: 12 }}>
          <div className="sub" style={{ marginBottom: 6 }}>Open a bank loan — capacity {fmtM(cap)}</div>
          <div className="opt-row" style={{ marginBottom: 8 }}>
            {tenors.map((t) => <button key={t.q} className={'opt-pill' + (loanTenor === t.q ? ' on' : '')} onClick={() => setLoanTenor(t.q)}>{t.label} · {(Eng.loanRate(g, H, t.q) * 100).toFixed(2)}%/q</button>)}
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <input className="fin-input" type="number" placeholder="Amount $" style={{ width: 160 }} value={loanAmt} onChange={(e) => setLoanAmt(e.target.value)} />
            <span className="sub">{quote}</span>
          </div>
          <button className="btn btn-pri btn-sm" style={{ marginTop: 8 }} disabled={!amt || amt > cap || loanCapHit || insolvent} title={insolvent ? "Banks won't lend to a company with negative book equity." : loanCapHit ? 'At most 10 loans per quarter' : undefined} onClick={() => { addLoan(loanTenor, amt); setLoanAmt(''); }}>Borrow →</button>
        </div>
        {(fin.loans?.length ?? 0) > 0 ? (
          <div className="sub" style={{ marginTop: 10 }}>Staged this quarter: {fin.loans!.map((ln, i) => <span key={i} className="chip acc" style={{ marginRight: 6 }}>{fmtM(ln.amount)} · {ln.tenor}q <span style={{ cursor: 'pointer', color: 'var(--bad)' }} onClick={() => removeLoan(i)}>✕</span></span>)}</div>
        ) : null}
      </div>
    );
  };

  // ── bonds ────────────────────────────────────────────────────────────────────
  const bondsPanel = () => {
    if (!instrOn('bond')) return null;
    const bondDebts = (g.debts as any[]).filter((d) => d.kind === 'bond');
    const bondOK = eq >= (g.bondThreshold || 40e6) && !['B', 'CCC'].includes(rating);
    const face = num(bondAmt);
    const capB = Math.max(0, 3 * eq - td);
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <h3>📜 Bonds<WhyChip /></h3>
        {bondDebts.length ? (
          <table className="tbl">
            <thead><tr><th>Facility</th><th className="num">Balance</th><th className="num">Rate/qtr</th><th className="num">Qtrs left</th><th>Repayment</th></tr></thead>
            <tbody>{bondDebts.map((d) => <tr key={d.id}><td>📜 Bond</td><td className="num">{fmtM(d.face)}</td><td className="num">{(d.rateQ * 100).toFixed(2)}%</td><td className="num">{d.qLeft}</td><td>{d.amort === 'bullet' ? 'bullet' : 'amortising'}</td></tr>)}</tbody>
          </table>
        ) : null}
        <div style={{ marginTop: 12 }}>
          <div className="sub" style={{ marginBottom: 6 }}>Issue a bond {bondOK ? '' : <span className="chip">locked</span>}</div>
          {!bondOK ? (
            <div className="sub">Bonds unlock at book equity ≥ {fmtM(g.bondThreshold || 40e6)} and an investment-grade rating (BBB or better). You’re at {fmtM(eq)} / {rating}.</div>
          ) : (
            <>
              <div className="opt-row" style={{ marginBottom: 8 }}>{([12, 16, 20] as const).map((q) => <button key={q} className={'opt-pill' + (bondTenor === q ? ' on' : '')} onClick={() => setBondTenor(q)}>{q}q · {(Eng.loanRate(g, H, q, true) * 100).toFixed(2)}%/q</button>)}</div>
              <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                <input className="fin-input" type="number" placeholder="Face $" style={{ width: 160 }} value={bondAmt} onChange={(e) => setBondAmt(e.target.value)} />
                <span className="sub">{!face ? '2% underwriting fee · bullet repayment.' : face > capB ? 'Max issue ≈ ' + fmtM(capB) + ' (keeps leverage sane).' : 'Coupon ' + (Eng.loanRate(g, H, bondTenor, true) * 100).toFixed(2) + '%/qtr · net proceeds ' + fmtM(face * 0.98) + ' (after 2% fee) · ' + fmtM(face) + ' balloon.'}</span>
              </div>
              <button className="btn btn-pri btn-sm" style={{ marginTop: 8 }} disabled={!face || face > capB || bondCapHit} title={bondCapHit ? 'At most 10 bonds per quarter' : undefined} onClick={() => { addBond(bondTenor, face); setBondAmt(''); }}>Issue bond →</button>
            </>
          )}
        </div>
        {(fin.bonds?.length ?? 0) > 0 ? (
          <div className="sub" style={{ marginTop: 10 }}>Staged: {fin.bonds!.map((bd, i) => <span key={i} className="chip acc" style={{ marginRight: 6 }}>{fmtM(bd.face)} · {bd.tenor}q <span style={{ cursor: 'pointer', color: 'var(--bad)' }} onClick={() => removeBond(i)}>✕</span></span>)}</div>
        ) : null}
      </div>
    );
  };

  // ── equity ───────────────────────────────────────────────────────────────────
  const equityPanel = () => {
    if (!instrOn('equity') && !instrOn('buyback')) return null;
    const issueP = price * 0.94, buyP = price * 1.04;
    const eAmt = num(eqAmt), bAmt = num(bbAmt);
    const ns = eAmt ? Math.round(eAmt / issueP) : 0;
    const afterRaise = ns ? g.ownerShares / (g.shares + ns) : pct;
    let bs = bAmt ? Math.round(bAmt / buyP) : 0; const bbNewTot = Math.max(g.ownerShares, g.shares - bs); bs = g.shares - bbNewTot;
    const afterBB = bs ? g.ownerShares / bbNewTot : pct;
    const bbCashAvail: number = cashNoBB ?? g.cash;
    const bbOverBuffer = bAmt > 0 && bAmt > bbCashAvail - 2e6;
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <h3>📈 Equity<WhyChip /></h3>
        {instrOn('equity') ? (
          <>
            <div className="sub" style={{ marginBottom: 6 }}>Raise capital — shares sold at ${issueP.toFixed(2)} (6% placement discount). Dilutes your stake but never burdens the balance sheet.</div>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <input className="fin-input" type="number" placeholder="Raise $" style={{ width: 160 }} value={eqAmt} onChange={(e) => setEqAmt(e.target.value)} />
              <span className="sub">{!eAmt ? 'Enter an amount to preview dilution.' : 'Issues ' + fmtK(ns) + ' shares · stake ' + Math.round(pct * 100) + '% → ' + Math.round(afterRaise * 100) + '% (' + Eng.controlBand(afterRaise).label + ')'}</span>
            </div>
            <button className="btn btn-pri btn-sm" style={{ marginTop: 8 }} disabled={!eAmt} onClick={() => { setEquityRaise(eAmt); setEqAmt(''); }}>Raise equity →</button>
            {(fin.equityRaise ?? 0) > 0 ? <div className="sub" style={{ marginTop: 8 }}>Staged raise: <span className="chip acc">{fmtM(fin.equityRaise!)} <span style={{ cursor: 'pointer', color: 'var(--bad)' }} onClick={() => setEquityRaise(0)}>✕</span></span></div> : null}
          </>
        ) : null}
        {instrOn('buyback') ? (
          <div style={{ marginTop: 14, borderTop: '1px solid var(--card-b)', paddingTop: 12 }}>
            <div className="sub" style={{ marginBottom: 6 }}>Buy back &amp; retire shares at ${buyP.toFixed(2)} (4% premium) — lifts your stake and EPS, your tool to defend control.</div>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <input className="fin-input" type="number" placeholder="Spend $" style={{ width: 160 }} value={bbAmt} onChange={(e) => setBbAmt(e.target.value)} />
              <span className="sub">{insolvent ? 'Buybacks are suspended while book equity is negative.' : !bAmt ? 'Spends cash to repurchase shares.' : bbOverBuffer ? 'Keep a $2M cash buffer — you can spend up to ' + fmtM(Math.max(0, bbCashAvail - 2e6)) + '.' : 'Retires ' + fmtK(bs) + ' shares · stake ' + Math.round(pct * 100) + '% → ' + Math.round(afterBB * 100) + '%'}</span>
            </div>
            <button className="btn btn-sm btn-ghost" style={{ marginTop: 8 }} disabled={!bAmt || bs <= 0 || bbOverBuffer || insolvent} onClick={() => { setBuyback(bAmt); setBbAmt(''); }}>Buy back →</button>
            {(fin.buyback ?? 0) > 0 ? <div className="sub" style={{ marginTop: 8 }}>Staged buyback: <span className="chip acc">{fmtM(fin.buyback!)} <span style={{ cursor: 'pointer', color: 'var(--bad)' }} onClick={() => setBuyback(0)}>✕</span></span></div> : null}
          </div>
        ) : null}
      </div>
    );
  };

  // ── dividends ────────────────────────────────────────────────────────────────
  const dividendPanel = () => {
    if (!instrOn('dividend')) return null;
    const estProfit = last ? Math.max(0, last.profit) : 0;
    const r = g.divRatio || 0;
    const estDiv = estProfit * r;
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <h3>💸 Dividend policy<WhyChip /></h3>
        <div className="sub" style={{ marginBottom: 8 }}>Payout ratio of net income, applied every profitable quarter until you change it. Paid out of retained earnings — it lowers book equity and competes with growth.</div>
        <div className="row" style={{ gap: 12, alignItems: 'center' }}>
          <input type="range" min={0} max={80} step={5} value={Math.round(r * 100)} style={{ flex: 1 }} disabled={insolvent} onChange={(e) => setDivRatio(parseFloat(e.target.value) / 100)} />
          <b style={{ width: 48 }}>{Math.round(r * 100)}%</b>
        </div>
        {insolvent ? <div className="sub" style={{ marginTop: 6, color: 'var(--bad)' }}>Dividends are suspended while book equity is negative.</div> : null}
        <div className="sub" style={{ marginTop: 8 }}>Est. next dividend ≈ {fmtM(estDiv)} · your {Math.round(pct * 100)}% share ≈ {fmtM(estDiv * pct)}.</div>
        <div className="sub" style={{ marginTop: 4 }}>Cumulative dividends paid: {fmtM(g.divPaidCum || 0)} · you’ve pocketed {fmtM(g.ownerWealth || 0)}.</div>
      </div>
    );
  };

  return (
    <div className="fn-root">
      <div className="screen-head">
        <h2>💰 Financing<WhyChip /></h2>
        <div className="sub">Fund the company: borrow, issue equity, set a dividend policy — each a real trade-off. You’re a public company, so equity is priced off your live stock.</div>
      </div>
      {overview()}
      {loansPanel()}
      {bondsPanel()}
      {equityPanel()}
      {dividendPanel()}
    </div>
  );
}
